import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import { Search, UserCog, Settings2, Trash2, Ban, CheckCircle2, ShieldQuestion } from "lucide-react";

const ROLE_TONE = { admin: "gold", parent: "primary", teacher: "mastered", student: "muted", disabled: "gap" };
const inputCls = "w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-2.5 focus:outline-none focus:border-[var(--ns-primary)]";

export default function AdminUsers() {
  const { user, impersonate } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [total, setTotal] = useState(0);
  const [manage, setManage] = useState(null);

  const load = async (query = "") => {
    const { data } = await api.get(`/admin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setUsers(data.users);
    setTotal(data.total);
  };
  useEffect(() => { load(); }, []);

  const doImpersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      impersonate(data.access_token, data.user);
      toast.success(`Now viewing as ${data.user.name || data.user.email}`);
      nav("/home");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not impersonate"); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-head text-2xl font-bold">User management <span className="text-sm font-medium text-[var(--ns-subtle)]">({total})</span></h2>
        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ns-subtle)]" />
            <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email"
              className={inputCls + " pl-9 w-64 max-w-full"} />
          </div>
          <Button type="submit" variant="outline" data-testid="user-search-btn">Search</Button>
        </form>
      </div>

      {!users ? <Spinner label="Loading users" /> : (
        <Card className="p-0 overflow-hidden">
          <div className="divide-y divide-[var(--ns-border)]">
            {users.length === 0 && <div className="p-6 text-center text-[var(--ns-muted)]">No users match that search.</div>}
            {users.map((u) => {
              const isSelf = u.id === user.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[var(--ns-text)] truncate">{u.name || "—"}</span>
                      <Pill tone={u.disabled ? "gap" : (ROLE_TONE[u.role] || "muted")}>{u.disabled ? "disabled" : u.role}</Pill>
                      {isSelf && <Pill tone="muted">you</Pill>}
                      {u.google_linked && <Pill tone="muted">Google</Pill>}
                    </div>
                    <div className="text-sm text-[var(--ns-muted)] truncate">{u.email}</div>
                    <div className="text-xs text-[var(--ns-subtle)] mt-0.5">
                      {u.auth_provider === "google" ? "Google sign-up" : "Email/password"} · joined {fmtDate(u.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isSelf && (
                      <Button variant="subtle" data-testid={`impersonate-btn-${u.id}`} onClick={() => doImpersonate(u)}>
                        <UserCog size={15} /> Impersonate
                      </Button>
                    )}
                    <Button variant="outline" data-testid={`manage-user-btn-${u.id}`} onClick={() => setManage(u)}>
                      <Settings2 size={15} /> Manage
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {manage && (
        <ManageUserModal
          u={manage}
          isSelf={manage.id === user.id}
          onClose={() => setManage(null)}
          onChanged={() => { load(q); }}
        />
      )}
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  try { return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

function ManageUserModal({ u, isSelf, onClose, onChanged }) {
  const [role, setRole] = useState(u.role);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);

  const saveRole = async () => {
    setBusy("role");
    try { await api.patch(`/admin/users/${u.id}/role`, { role }); toast.success("Role updated"); onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const savePw = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy("pw");
    try { await api.post(`/admin/users/${u.id}/password`, { new_password: pw }); toast.success("Password set"); setPw(""); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const toggleStatus = async () => {
    setBusy("status");
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/status`, { disabled: !u.disabled });
      toast.success(data.disabled ? "Account disabled" : "Account re-enabled");
      onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const del = async () => {
    setBusy("del");
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Account deleted${data.students_deleted ? ` (+${data.students_deleted} learner${data.students_deleted > 1 ? "s" : ""})` : ""}`);
      onChanged(); onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center bg-black/40 p-5 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-md p-6 ns-rise my-6" onClick={(e) => e.stopPropagation()} data-testid="manage-user-modal">
        <h3 className="font-head text-xl font-bold text-[var(--ns-text)]">Manage user</h3>
        <p className="text-sm text-[var(--ns-muted)] mb-5">{u.name} · {u.email}</p>

        <div className="grid gap-5">
          <div>
            <label className="text-sm font-semibold text-[var(--ns-text)]">Role</label>
            <div className="mt-2 flex gap-2">
              <select data-testid="role-select" value={role} onChange={(e) => setRole(e.target.value)} disabled={isSelf}
                className={inputCls + " flex-1 disabled:opacity-60"}>
                {["parent", "student", "teacher", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <Button data-testid="save-role-btn" onClick={saveRole} disabled={isSelf || busy === "role" || role === u.role}>Save</Button>
            </div>
            {isSelf && <p className="text-xs text-[var(--ns-subtle)] mt-1">You can't change your own role.</p>}
          </div>

          <div>
            <label className="text-sm font-semibold text-[var(--ns-text)]">Set a new password</label>
            <div className="mt-2 flex gap-2">
              <input data-testid="set-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                className={inputCls + " flex-1"} />
              <Button data-testid="save-password-btn" onClick={savePw} disabled={busy === "pw" || !pw}>Set</Button>
            </div>
          </div>

          <div className="border-t border-[var(--ns-border)] pt-4 grid gap-3">
            {!isSelf && (
              <Button variant="outline" data-testid="toggle-status-btn" onClick={toggleStatus} disabled={busy === "status"}
                className={u.disabled ? "!border-[#2A8555] !text-[#2A8555]" : "!border-[#B23A28] !text-[#B23A28]"}>
                {u.disabled ? <><CheckCircle2 size={16} /> Re-enable account</> : <><Ban size={16} /> Disable account</>}
              </Button>
            )}
            {!isSelf && (confirmDel ? (
              <div className="rounded-[var(--ns-radius)] bg-[#FDECE8] border border-[#F3C9BF] p-3">
                <p className="text-sm text-[#8A2C1D] flex items-start gap-2"><ShieldQuestion size={16} className="mt-0.5" /> Delete this account and all linked learners &amp; data? This cannot be undone.</p>
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setConfirmDel(false)}>Cancel</Button>
                  <Button data-testid="confirm-delete-btn" onClick={del} disabled={busy === "del"} className="!bg-[#B23A28] hover:!bg-[#8A2C1D]">{busy === "del" ? "Deleting…" : "Delete"}</Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" data-testid="delete-user-btn" onClick={() => setConfirmDel(true)} className="!border-[#B23A28] !text-[#B23A28]">
                <Trash2 size={16} /> Delete account
              </Button>
            ))}
            {isSelf && <p className="text-xs text-[var(--ns-subtle)]">Disable and delete are unavailable for your own account.</p>}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  );
}
